import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiX, FiPhone, FiMail } from 'react-icons/fi';
import useStickyHeader from './useStickyHeader';
import { switchLanguagePath, getLanguageFromPath } from '../../lib/languagePaths';

const LOGO = `${process.env.PUBLIC_URL}/images/logo/logo-cyberate.png`;

const PillarDropdown = ({ t, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const id = 'invest-dropdown';

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDoc);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDoc);
    };
  }, [open]);

  return (
    <div className="cy-dropdown" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className="cy-header__nav-btn"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        {t('nav.whatWeInvest')} <FiChevronDown aria-hidden="true" />
      </button>
      {open && (
        <ul className="cy-dropdown__menu" id={id}>
          <li>
            <Link to="/property-development" onClick={() => { setOpen(false); onNavigate && onNavigate(); }}>
              {t('nav.pillars.property.label')}
              <span>{t('nav.pillars.property.desc')}</span>
            </Link>
          </li>
          <li>
            <Link to="/built-environment-investments" onClick={() => { setOpen(false); onNavigate && onNavigate(); }}>
              {t('nav.pillars.built.label')}
              <span>{t('nav.pillars.built.desc')}</span>
            </Link>
          </li>
          <li>
            <Link to="/innovation-ventures" onClick={() => { setOpen(false); onNavigate && onNavigate(); }}>
              {t('nav.pillars.innovation.label')}
              <span>{t('nav.pillars.innovation.desc')}</span>
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
};

const LanguageToggle = () => {
  const location = useLocation();
  const fullPath = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
  const isEn = getLanguageFromPath(fullPath) === 'en';
  // 语言切换 = 全页跳转到同页面的另一语言版本（URL 反映语言，爬虫友好）。
  // Router basename 会剥掉 /zh，所以判断与跳转都要用 window.location.pathname。
  const switchTo = (lng) => {
    const nextPath = switchLanguagePath(fullPath, location.search, location.hash, lng);
    window.location.href = nextPath;
  };
  return (
    <span className="cy-lang">
      <button type="button" className={isEn ? 'is-active' : ''} aria-current={isEn ? 'true' : undefined} onClick={() => switchTo('en')}>
        EN
      </button>
      <span aria-hidden="true" style={{ color: 'var(--cy-color-border-input)' }}>|</span>
      <button type="button" className={!isEn ? 'is-active' : ''} aria-current={!isEn ? 'true' : undefined} onClick={() => switchTo('zh')}>
        中文
      </button>
    </span>
  );
};

const HeaderOne = () => {
  const { t } = useTranslation('cy');
  const sticky = useStickyHeader(8);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const drawerRef = useRef(null);
  const burgerRef = useRef(null);

  useEffect(() => {
    const lang = getLanguageFromPath(typeof window !== 'undefined' ? window.location.pathname : location.pathname) === 'en' ? 'en-AU' : 'zh-CN';
    document.documentElement.lang = lang;
  }, [location.pathname]);

  useEffect(() => {
    setDrawerOpen(false);
    setGroupOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const first = drawerRef.current?.querySelector('a, button');
    first?.focus();
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  return (
    <>
      <a className="cy-skip-link" href="#main">
        {t('ui.skipToContent')}
      </a>
      <header className={`cy-header${sticky ? ' is-sticky' : ''}`}>
        <div className="cy-container cy-header__inner">
          <Link to="/" className="cy-header__logo" aria-label={t('ui.logoHome')}>
            <img src={LOGO} alt="Cyberate Investments" />
          </Link>

          <nav className="cy-header__nav" aria-label={t('nav.aria')}>
            <NavLink to="/" exact>
              {t('nav.home')}
            </NavLink>
            <PillarDropdown t={t} />
            <NavLink to="/approach">{t('nav.howWeInvest')}</NavLink>
            <NavLink to="/about">{t('nav.about')}</NavLink>
            <NavLink to="/contact">{t('nav.contact')}</NavLink>
          </nav>

          <div className="cy-header__right">
            <LanguageToggle />
            <Link to="/submit-opportunity" className="cy-btn cy-btn--primary cy-btn--small">
              {t('nav.submitCta')}
            </Link>
            <button
              ref={burgerRef}
              type="button"
              className={`cy-hamburger${drawerOpen ? ' is-open' : ''}`}
              aria-label={t('ui.toggleMenu')}
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {drawerOpen && (
        <div className="cy-drawer-scrim" onClick={() => setDrawerOpen(false)} />
      )}
      <div
        className="cy-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.aria')}
        aria-hidden={!drawerOpen}
        style={{
          transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms var(--cy-ease-in-out)',
          visibility: drawerOpen ? 'visible' : 'hidden',
        }}
      >
        <div className="cy-drawer__head">
          <img src={LOGO} alt="Cyberate Investments" />
          <button type="button" className="cy-drawer__close" aria-label={t('ui.closeMenu')} onClick={() => setDrawerOpen(false)}>
            <FiX />
          </button>
        </div>
        <ul className="cy-drawer__list">
          <li>
            <Link to="/submit-opportunity" className="cy-btn cy-btn--primary cy-btn--small" style={{ width: '100%' }}>
              {t('nav.submitCta')}
            </Link>
          </li>
          <li>
            <Link to="/">{t('nav.home')}</Link>
          </li>
          <li>
            <button type="button" aria-expanded={groupOpen} onClick={() => setGroupOpen((v) => !v)}>
              {t('nav.whatWeInvest')} <FiChevronDown aria-hidden="true" />
            </button>
            {groupOpen && (
              <ul className="cy-drawer__group">
                <li>
                  <Link to="/property-development">{t('nav.pillars.property.label')}</Link>
                </li>
                <li>
                  <Link to="/built-environment-investments">{t('nav.pillars.built.label')}</Link>
                </li>
                <li>
                  <Link to="/innovation-ventures">{t('nav.pillars.innovation.label')}</Link>
                </li>
              </ul>
            )}
          </li>
          <li>
            <Link to="/approach">{t('nav.howWeInvest')}</Link>
          </li>
          <li>
            <Link to="/about">{t('nav.about')}</Link>
          </li>
          <li>
            <Link to="/contact">{t('nav.contact')}</Link>
          </li>
          <li style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <LanguageToggle />
          </li>
          <li style={{ display: 'flex', gap: 16, fontSize: 14, flexWrap: 'wrap' }}>
            <a className="cy-link" href="tel:+61871100111" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FiPhone aria-hidden="true" /> +61 8 7110 0111
            </a>
            <a className="cy-link" href="mailto:info@cyberate.investments" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FiMail aria-hidden="true" /> info@cyberate.investments
            </a>
          </li>
        </ul>
      </div>
    </>
  );
};

export default HeaderOne;
